/**
 * Allergens and additives (data/allergens.json): the 14 main allergens of the food information
 * regulation (LMIV, annex II) and the 14 additives German menus name. One fixed English id each, as
 * SpeisePlan keys them ("gluten", "milk"); the code menus print (a–n, 1–14); a German name and a
 * short one. Before this, every app had its own list: Lagerlog (drinks.ts, German names as values),
 * SpeiseKarte (allergens.ts, codes), SpeisePlan (properties.ts, English keys). findAllergen reads
 * all of their spellings, so stored values can be mapped to the ids.
 *
 * In the seven languages of the cards, as SpeiseKarte prints them since 01.11.2026 (Turkish since 03.11.2026), kept apart:
 * - the 14 allergens' `names` (allergenName) are official and fixed: word for word from annex II of
 *   Regulation (EU) No 1169/2011 in each language (EUR-Lex, CELEX 32011R1169, read on 01.11.2026 by
 *   SpeiseKarte), shortened to the head of each item. Turkish, which EUR-Lex lacks, word for word from
 *   Ek-1 of the Türk Gıda Kodeksi Gıda Etiketleme ve Tüketicileri Bilgilendirme Yönetmeliği (Resmî
 *   Gazete 29960 Mükerrer, 26.01.2017, page 982), the same 14 in the same order. Never reworded,
 *   never changed by a business.
 * - the additives' `defaults` (additiveName) are only a default: additives are not set by the EU
 *   (Martin, 01.11.2026), their numbers and words are German practice under national law, their
 *   translations our proposal, which a business may override with its own (sweetener and
 *   phenylalanine follow the wording of annex III, in Turkish of Ek-2 2.1 and 2.3, high caffeine of 4.1). The same stand in the base vocabulary of lang.
 *
 * The German name of additive 7, "mit Süßungsmittel", departs from annex III ("mit Süßungsmittel(n)",
 * the English "with sweetener(s)" kept as worded there) on purpose: it is the name in the businesses'
 * catalogues, and the apps know the default by it (Martin, 01.11.2026).
 */
import data from "../data/allergens.json" with { type: "json" };
import { fold } from "./normalize.ts";

export interface Allergen {
  /** Fixed: "gluten", "crustaceans" … */
  id: string;
  /** What menus print: "a" to "n". */
  code: string;
  /** For menus and labels: "Glutenhaltiges Getreide". */
  name: string;
  /** Short, for a chip or a filter: "Gluten". */
  short: string;
  /** Other spellings the apps used: "Milch", "Nüsse", "Sulfite". */
  aliases: readonly string[];
  /** Official, from EUR-Lex, in the languages of the cards: "Cereals containing gluten". */
  names: AllergenNames;
}

export interface Additive {
  /** Fixed: "colouring", "preservative" … */
  id: string;
  /** What menus print: "1" to "14". */
  code: string;
  /** "mit Farbstoff". */
  name: string;
  /** A default in the languages of the cards, a business may override it: "with colouring". */
  defaults: AllergenNames;
}

/** The languages of the cards besides German, as the part lang has them. */
export const ALLERGEN_LANGS = ["en", "es", "fr", "it", "nl", "pt", "tr"] as const;
export type AllergenLang = (typeof ALLERGEN_LANGS)[number];
export type AllergenNames = Readonly<Record<AllergenLang, string>>;

/** The 14, in the order of annex II (= the order of the codes a–n). */
export const ALLERGENS: readonly Allergen[] = data.allergens;
/**
 * The 14, in the order of their numbers: 1–13 as SpeiseKarte has them, 14 „erhöhter Koffeingehalt“
 * from KellerLog. Its defaults are the head of annex III 4.1 in each language, up to the first full
 * stop, colon or comma (EUR-Lex, read 01.11.2026), in lower case like the others; the full text also
 * advises against it for children and pregnant or breast-feeding women.
 */
export const ADDITIVES: readonly Additive[] = data.additives;

const ALLERGEN_BY_ID = new Map(ALLERGENS.map((a) => [a.id, a]));
const ADDITIVE_BY_ID = new Map(ADDITIVES.map((a) => [a.id, a]));
const BY_CODE = new Map<string, Allergen | Additive>([...ALLERGENS, ...ADDITIVES].map((a) => [a.code, a]));

export const allergen = (id: unknown): Allergen | undefined => (typeof id === "string" ? ALLERGEN_BY_ID.get(id) : undefined);
export const additive = (id: unknown): Additive | undefined => (typeof id === "string" ? ADDITIVE_BY_ID.get(id) : undefined);

const inLang = (de: string, names: AllergenNames, lang: string) => (lang === "de" ? de : Object.hasOwn(names, lang) ? names[lang as AllergenLang] : undefined);

/**
 * An allergen's official name in a language of the cards, by its id: "de" the German name, "en"
 * "Cereals containing gluten". Fixed; no business translates it. Undefined for an unknown id or
 * language, and for an additive (additiveName).
 */
export function allergenName(id: string, lang: string): string | undefined {
  const a = ALLERGEN_BY_ID.get(id);
  return a && inLang(a.name, a.names, lang);
}

/**
 * An additive's default name in a language of the cards, by its id: "en" "with colouring". Only a
 * proposal: the business's own translation comes first. Undefined for an unknown id or language.
 */
export function additiveName(id: string, lang: string): string | undefined {
  const a = ADDITIVE_BY_ID.get(id);
  return a && inLang(a.name, a.defaults, lang);
}

/** A code as menus write it: "a", "A)", "(c)", " 12 ". Undefined when no allergen or additive has it. */
export function byCode(raw: string): Allergen | Additive | undefined {
  return BY_CODE.get(raw.trim().toLowerCase().replace(/^[([]+|[)\].]+$/g, ""));
}

const ADDITIVE_BY_NAME = new Map(ADDITIVES.map((a) => [fold(a.name), a]));

/** The additive a German name stands for, as the apps store them: "mit Süßungsmittel", "Erhöhter Koffeingehalt". */
export function findAdditive(name: string): Additive | undefined {
  return ADDITIVE_BY_NAME.get(fold(name));
}

const BY_NAME = new Map<string, Allergen>();
for (const a of ALLERGENS) for (const n of [a.id, a.name, a.short, ...a.aliases]) BY_NAME.set(fold(n), a);

/**
 * The allergen a name stands for, in any spelling of the apps: "Glutenhaltiges Getreide",
 * "Gluten", "Milch", "Milch und Laktose", "Schalenfrüchte (Nüsse)", "Eier", "eggs". Case and
 * umlauts do not matter. Not a reading of ingredients ("Weizenmehl" is no name of an allergen):
 * that stays with SpeisePlan.
 */
export function findAllergen(name: string): Allergen | undefined {
  return BY_NAME.get(fold(name));
}

/** Allergen ids in the order of annex II; unknown ones last, as they came. */
export function sortAllergens(ids: readonly string[]): string[] {
  const order = new Map(ALLERGENS.map((a, i) => [a.id, i]));
  return [...ids].sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99));
}
